import { defineStore } from 'pinia';
import { computed } from 'vue';

import type {
  DetailsStaticInfo,
  HeartedHotel,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';

import type {
  HotelStaticData,
  HotelDynamicData,
  SelectStayEventPayload,
  ToggleHeartEventPayload,
} from '../../modules/hotelDetails';
import { HotelFilter } from '../../modules/hotelFilters';
import type { HotelFilterMealsUpdate } from '../../modules/hotelFilters';
import { RemoveHotelSelectionFiltersOrigin } from '../models';

import { useRouterStore } from './routerStore';
import { useFiltersStore } from './filters/filtersStore';
import { useRecipeStore } from './recipe/recipeStore';
import { useTripStore } from './trip/tripStore';
import { useProductDetailStore } from './productDetail/productDetailStore';
import { useMapStore } from './mapStore/mapStore';
import { useEditingIngredientStore } from './editingIngredient/editingIngredientStore';
import { usePreferredTimeStore } from './preferredTimeStore';

export const useHotelDetailsStore = defineStore('HotelDetailsStore', () => {
  const mapStore = useMapStore();
  const productDetailStore = useProductDetailStore();
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();
  const editingIngredientStore = useEditingIngredientStore();
  const routerStore = useRouterStore();
  const preferredTimeStore = usePreferredTimeStore();
  const filtersStore = useFiltersStore();

  // Actions
  function openHotelDetails(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailStore.updateShouldScrollToAvailability(
      shouldScrollToAvailability
    );
    productDetailStore.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  // NOTE: Temporary method required until we will be able to
  // use hotel details panel on separated route on production.
  function openHotelDetailsForce(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailStore.updateShouldScrollToAvailability(
      shouldScrollToAvailability
    );
    productDetailStore.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  function selectStay(eventPayload: SelectStayEventPayload): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
      isRefundable: eventPayload.stayId.isRefundable,
      rooms: eventPayload.stayId.hotelRoomIds.map((room) => ({
        code: room.roomCode,
        boardBasis: room.boardBasis,
        roomTypeId: room.roomTypeId,
      })),
    };
    tripStore.toggleHeartHotel(heartedHotel, eventPayload.ingredientId);
    mapStore.updateMapShouldCenterOnMarkers(true);
    editingIngredientStore.doneEditingIngredient();
  }

  function toggleHeart(
    eventPayload: ToggleHeartEventPayload,
    finalizeEdit = true
  ): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
    };
    tripStore.toggleHeartHotel(heartedHotel, eventPayload.ingredientId);

    if (finalizeEdit) {
      editingIngredientStore.doneEditingIngredient();
    }
  }

  function suggestAnotherDay(): void {
    routerStore.navigateToTripOverview();
    preferredTimeStore.clearPreferredDay();
  }

  function updateMealFilter(event: {
    filter: HotelFilterMealsUpdate;
    source: RemoveHotelSelectionFiltersOrigin;
  }): void {
    filtersStore.updateHotelFilter(event.filter, event.source);
  }

  function toggleFreeCancellationFilter(isChecked: boolean): void {
    filtersStore.updateHotelFilter(
      {
        type: HotelFilter.RuleFreeCancellation,
        value: isChecked,
      },
      RemoveHotelSelectionFiltersOrigin.HotelDetails
    );
  }

  // Getters
  const hotelDetailsStaticData = computed<HotelStaticData | undefined>(() => {
    const staticHotelData =
      productDetailStore.destinationStaticProductDetailForHotel;

    if (!staticHotelData) {
      return;
    }

    return {
      ingredientId: staticHotelData.ingredientId,
      hotelCode: staticHotelData.productCode,
      isAvailabilityLoading: staticHotelData.isAvailabilityLoading,
      staticInfo: staticHotelData.staticInfo as DetailsStaticInfo,
      isHearted: staticHotelData.isHearted,
      isInItinerary: staticHotelData.isInItinerary,
      numberOfNights: staticHotelData.numberOfNights,
      scrollToAvailability: productDetailStore.shouldScrollToAvailability,
      destinationInformation: tripStore.getDestinationInformation(
        staticHotelData.ingredientId
      ),
      isSingleHotelRecipe:
        recipeStore.recipeWithoutOwnTransport.length === 1 &&
        recipeStore.hotelOnlyRecipe.length === 1,
    };
  });

  const hotelDetailsDynamicData = computed<HotelDynamicData | undefined>(() => {
    const hotelDynamicData =
      productDetailStore.destinationDynamicProductDetailForHotel;

    if (!hotelDynamicData) {
      return;
    }

    return {
      availability: hotelDynamicData.availability,
      isMetaHotel: hotelDynamicData.availability.some(
        (bookableOption) => bookableOption.isFromMeta
      ),
    };
  });

  return {
    // Actions
    openHotelDetails,
    openHotelDetailsForce,
    selectStay,
    toggleHeart,
    suggestAnotherDay,
    updateMealFilter,
    toggleFreeCancellationFilter,

    // Getters

    hotelDetailsStaticData,
    hotelDetailsDynamicData,
  };
});
